import { Badge, Button, Flex, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted.tsx';
import { InvestorDetailsRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsRow/InvestorDetailsRow.tsx';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorHoldingRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.types.ts';
import { canExpandVesting } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingsCard/investorHoldings.utils.ts';
import { SecurityIssuedIn } from '@dealstack/shared/lib/generated';
import { formatSecurityUnits } from '@dealstack/shared/lib/format/number';

interface Props {
	holding: InvestorHoldingRow;
	securityTypeLabel: string;
	isExpanded: boolean;
	heldColumn?: 'via' | 'for';
	onToggleVesting: () => void;
}

export const HoldingCardItem = ({
	holding,
	securityTypeLabel,
	isExpanded,
	heldColumn = 'via',
	onToggleVesting,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const amount = (value: number | null) => (
		<AmountFormatted
			value={value}
			currency={holding.currency}
			showZero
			showNegative
			decimalScale={2}
			textProps={{
				fw: 600,
				style: { fontVariantNumeric: 'lining-nums tabular-nums' },
			}}
		/>
	);

	return (
		<div
			className={['holding', isExpanded && 'expanded'].filter(Boolean).join(' ')}
			data-testid={makeComplexDataTestId(
				TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_CARD,
				holding.id
			)}
		>
			<Flex direction="column" gap="0.25rem" className="heading">
				<Flex align="center" justify="space-between" gap="0.5rem">
					<Text fz={14} fw={600} c="dark.0" truncate>
						{holding.issuerName}
					</Text>

					{canExpandVesting(holding) && (
						<Badge
							variant="outline"
							color="gray.4"
							c="dark.4"
							leftSection={<Icon variant="checkmark-circle" size="md" />}
						>
							<Text fz={12}>
								{tMipManage('investors.details.holdings_table.vesting_badge')}
							</Text>
						</Badge>
					)}
				</Flex>

				<Text fz={18} fw={600} c="dark.8" truncate>
					{holding.securityName}
				</Text>

				<Text fz={14} c="dark.0" truncate>
					{securityTypeLabel}
				</Text>
			</Flex>

			{heldColumn === 'for'
				? !!holding.heldForName && (
						<InvestorDetailsRow
							label={tMipManage('investors.details.holdings_table.columns.held_for')}
							value={holding.heldForName}
							padding="0.25rem 0"
						/>
					)
				: !!holding.heldViaName && (
						<InvestorDetailsRow
							label={tMipManage('investors.details.holdings_table.columns.held_via')}
							value={holding.heldViaName}
							padding="0.25rem 0"
						/>
					)}

			<InvestorDetailsRow
				label={tMipManage('investors.details.holdings_table.columns.units')}
				value={formatSecurityUnits(holding.units, {
					unitDenominator: holding.securityUnitDenominator,
					isIssuedInUnits: holding.securityIssuedIn === SecurityIssuedIn.UNITS,
				})}
				padding="0.25rem 0"
			/>

			<InvestorDetailsRow
				label={tMipManage('investors.details.holdings_table.columns.total_cost_act')}
				value={amount(holding.totalCostActual)}
				padding="0.25rem 0"
			/>

			<InvestorDetailsRow
				label={tMipManage('investors.details.holdings_table.columns.total_cost_ave')}
				value={amount(holding.totalCostAverage)}
				padding="0.25rem 0"
			/>

			<InvestorDetailsRow
				label={tMipManage('investors.details.holdings_table.columns.current_value')}
				value={amount(holding.currentValue)}
				padding="0.25rem 0"
			/>

			{canExpandVesting(holding) && (
				<div className="vesting">
					<Button
						variant="transparent"
						size="md"
						fw={400}
						px={0}
						onClick={onToggleVesting}
						rightSection={
							<Icon variant={isExpanded ? 'caret-up' : 'caret-down'} size="lg" />
						}
						data-testid={makeComplexDataTestId(
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_VESTING_TOGGLE,
							holding.securityId
						)}
					>
						{isExpanded
							? tMipManage('investors.details.holdings_table.hide_vesting')
							: tMipManage('investors.details.holdings_table.show_vesting')}
					</Button>
				</div>
			)}
		</div>
	);
};
